import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaCopy, FaCheck, FaWhatsapp } from 'react-icons/fa';
import Navbar from '../components/Navbar';
import { useSiteSettings } from '../hooks/useSiteSettings';
import { whatsappHref, formatPhoneDisplay } from '../utils/contactLinks';
import { accountsForDonateContext, formatRib } from '../config/donateBanks';
import './PublicSimplePages.css';

const DonatePage = () => {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const settings = useSiteSettings();
  const [copiedId, setCopiedId] = useState('');
  const program = params.get('program');
  const projet = params.get('projet');

  const contextLabel = useMemo(() => {
    if (projet) return t('public.donatePage.forProject');
    if (program) return t('public.donatePage.forProgram');
    return t('public.donatePage.now');
  }, [program, projet, t]);

  const accounts = useMemo(() => accountsForDonateContext(program), [program]);
  const whatsappNumber = settings.whatsapp || settings.phone || '';
  const wa = whatsappHref(whatsappNumber);
  const waLabel = formatPhoneDisplay(whatsappNumber) || whatsappNumber;

  const copyRib = async (account) => {
    try {
      await navigator.clipboard.writeText(account.rib);
      setCopiedId(account.id);
      setTimeout(() => setCopiedId(''), 2000);
    } catch {
      setCopiedId('');
    }
  };

  return (
    <div className="public-simple-page">
      <Helmet>
        <title>{`${t('public.donatePage.metaTitle')} | ${t('public.brand.titleSuffix')}`}</title>
      </Helmet>
      <Navbar />
      <main className="public-simple-page__main">
        <h1>{contextLabel}</h1>
        <p className="public-simple-page__lead">
          {settings.donate_intro || t('public.donatePage.intro')}
        </p>

        <div className="donate-rib-list">
          {accounts.map((account) => (
            <article key={account.id} className={`donate-rib-card donate-rib-card--${account.theme}`}>
              <div className="donate-rib-card__bank">
                <strong>{account.nameAr}</strong>
                <span>{account.nameFr}</span>
              </div>
              <p className="donate-rib-card__label">{t('public.donatePage.ribLabel')}</p>
              <p className="donate-rib-card__rib" dir="ltr">{formatRib(account.rib)}</p>
              {account.swift ? (
                <p className="donate-rib-card__swift" dir="ltr">
                  SWIFT : {account.swift}
                </p>
              ) : null}
              <button type="button" className="donate-rib-card__copy" onClick={() => copyRib(account)}>
                {copiedId === account.id ? <FaCheck /> : <FaCopy />}
                {copiedId === account.id ? t('public.donatePage.copied') : t('public.donatePage.copyRib')}
              </button>
            </article>
          ))}
        </div>

        <div className="public-simple-card">
          <h2>{t('public.donatePage.whatsapp')}</h2>
          {wa ? (
            <>
              <p className="public-simple-whatsapp-number" dir="ltr">
                {waLabel}
              </p>
              <a className="public-simple-btn public-simple-btn--wa" href={wa} target="_blank" rel="noopener noreferrer">
                <FaWhatsapp /> {t('public.donatePage.whatsappCta')}
              </a>
            </>
          ) : (
            <p className="public-simple-page__muted">{t('public.donatePage.whatsappMissing')}</p>
          )}
        </div>
      </main>
    </div>
  );
};

export default DonatePage;
